/**
 * Lifecycle state of a campaign.
 *
 * @remarks
 * Values match the contract persisted by the backend, so they must not be
 * translated or renamed. The backend coerces the legacy `DRAFT`/`PUBLISHED`
 * values into these on read, so the frontend never sees them.
 */
export enum CampaignStatus {
  Active = 'ACTIVE',
  Paused = 'PAUSED',
  Inactive = 'INACTIVE',
}

/** Order the status options are offered in the form. */
export const CAMPAIGN_STATUS_OPTIONS: CampaignStatus[] = [
  CampaignStatus.Active,
  CampaignStatus.Paused,
  CampaignStatus.Inactive,
];

/**
 * Campaign published to the isoFarm support portal.
 *
 * @remarks
 * `url` and `announceOnLogin` are absent on campaigns registered before those
 * fields existed, so readers must treat them as optional rather than assume a
 * value. An absent `announceOnLogin` means the campaign stays out of the
 * dashboard carousel; the field keeps its original name because it is persisted
 * — renaming it would unflag every campaign already stored.
 */
export interface Campaign {
  id: string;
  title: string;
  slug?: string;
  description: string;
  url?: string | null;
  announceOnLogin?: boolean;
  imageUrl: string | null;
  status: CampaignStatus;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

/** Payload accepted when creating a campaign. */
export interface CreateCampaignDto {
  title: string;
  description: string;
  status: CampaignStatus;
  url?: string | null;
  announceOnLogin?: boolean;
}

/** Payload accepted when updating an existing campaign. */
export interface UpdateCampaignDto {
  title: string;
  description: string;
  status?: CampaignStatus;
  url?: string | null;
  announceOnLogin?: boolean;
  imageUrl?: string | null;
}

/**
 * Campaign as delivered to the dashboard carousel.
 *
 * @remarks
 * A distinct shape from {@link Campaign}: the backend only returns campaigns
 * that carry both an image and a URL, so both are guaranteed here.
 *
 * `createdAt` arrives as an ISO string and is formatted client-side, in the
 * language the user is actually reading.
 */
export interface CampaignCarouselSlide {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  url: string;
  createdAt: string;
}

/** Modes supported by the campaign form modal. */
export type CampaignFormMode = 'create' | 'edit' | 'view';

/** Data injected into the campaign form modal. */
export interface CampaignFormModalData {
  mode: CampaignFormMode;
  campaign?: Campaign;
}

/** Result emitted when the campaign form modal is closed by a submit. */
export interface CampaignFormModalResult {
  mode: CampaignFormMode;
  campaign: Campaign;
}

/**
 * Panel class applied to every campaign dialog.
 *
 * @remarks
 * Zeroes the Material dialog padding (rule in `styles.scss`) so the header
 * divider and the footer band reach the modal edges, as the design requires.
 */
export const CAMPAIGN_DIALOG_PANEL_CLASS = 'campaign-dialog-panel';

export const CAMPAIGN_TITLE_MAX_LENGTH = 120;

export const CAMPAIGN_DESCRIPTION_MAX_LENGTH = 280;

export const CAMPAIGN_URL_MAX_LENGTH = 2048;

/** Schemes a campaign URL may use. */
const CAMPAIGN_URL_ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Tells whether a pasted address is usable as a campaign URL.
 *
 * @param value - Raw input, already trimmed by the caller.
 * @returns `true` for an absolute `http`/`https` address.
 *
 * @remarks
 * Only `http`/`https` are accepted: the URL is meant to be opened by whoever
 * receives the campaign, and `javascript:` or `data:` addresses would turn that
 * into an injection vector. Mirrored by the backend, which validates the same
 * way — the frontend check is convenience, not the guarantee.
 */
export function isValidCampaignUrl(value: string): boolean {
  let parsed: URL;

  try {
    parsed = new URL(value);
  } catch {
    return false;
  }

  return CAMPAIGN_URL_ALLOWED_PROTOCOLS.includes(parsed.protocol);
}

export const CAMPAIGN_IMAGE_MAX_SIZE_MB = 1;

const BYTES_PER_KB = 1024;
const KB_PER_MB = 1024;

export const CAMPAIGN_IMAGE_MAX_SIZE_BYTES =
  CAMPAIGN_IMAGE_MAX_SIZE_MB * KB_PER_MB * BYTES_PER_KB;

/**
 * Media types a campaign image may declare.
 *
 * @remarks
 * `.jpg` and `.jpeg` both report `image/jpeg`, so the two extensions need no
 * separate entry here.
 */
export const CAMPAIGN_IMAGE_ACCEPTED_MIME_TYPES = ['image/png', 'image/jpeg'];

export const CAMPAIGN_IMAGE_ACCEPTED_EXTENSIONS = ['.png', '.jpg', '.jpeg'];

/** Value handed to the file input's `accept`, kept in step with the list above. */
export const CAMPAIGN_IMAGE_ACCEPT_ATTRIBUTE =
  CAMPAIGN_IMAGE_ACCEPTED_EXTENSIONS.join(',');

/**
 * Tells whether a picked file carries an accepted image extension.
 *
 * @param fileName - Name of the file as reported by the browser.
 *
 * @remarks
 * Checked alongside the media type rather than instead of it: a file the browser
 * could not type reaches us with an empty `type`, and one renamed to `.png`
 * reaches us typed as an image. Neither check is the guarantee — the backend
 * inspects the actual magic bytes — but together they stop the obvious mistakes
 * before an upload is attempted.
 */
export function hasAcceptedImageExtension(fileName: string): boolean {
  const normalized = fileName.trim().toLowerCase();

  return CAMPAIGN_IMAGE_ACCEPTED_EXTENSIONS.some((extension) =>
    normalized.endsWith(extension),
  );
}
